/*
 * Ends a session the server has stopped accepting. A rejected sign-in token comes
 * back as a 401 carrying a SESSION_* reason; staff get one silent refresh first,
 * parents go straight back to OTP sign-in. See "Rejected credentials — sign-in vs.
 * link" in CLAUDE.md.
 */
import { ApolloClient, Observable } from '@apollo/client';
import { onError } from '@apollo/client/link/error';

import { LOGIN_ROUTE } from '../../../_configuration';
import { readSessionFailure } from '../helpers/graphql-error.helpers';
import { parentLoginPathWithNext } from '../helpers/parent-links.helpers';
import { teardownSession } from '../services/auth.service';
import { SessionType } from '../types/session.types';
import { AuthApiService, REFRESH_RETRY } from './auth-api.service';

export interface ISessionExpiryDeps {
  refreshStaffToken: () => Promise<string | null>;
  teardown: (tree: SessionType, client: ApolloClient<unknown>) => Promise<void>;
  location: Pick<Location, 'pathname' | 'search' | 'assign' | 'reload'>;
}

const defaultDeps = (): ISessionExpiryDeps => ({
  refreshStaffToken: () => AuthApiService.refreshStaffToken(),
  teardown: teardownSession,
  location: window.location,
});

export const createSessionExpiryLink = (
  tree: SessionType,
  getClient: () => ApolloClient<unknown> | undefined,
  deps: ISessionExpiryDeps = defaultDeps(),
) => {
  // Many requests fail together when a token dies: one refresh and one teardown serve them all.
  let refreshing: Promise<string | null> | null = null;
  let ending = false;

  const endSession = async () => {
    if (ending) return;
    ending = true;

    const client = getClient();
    try {
      if (client) await deps.teardown(tree, client);
    } catch (err) {
      console.error('Session teardown after a rejected sign-in failed:', err);
    }

    const { location } = deps;
    if (tree === SessionType.STAFF) {
      location.assign(LOGIN_ROUTE);
    } else if (new URLSearchParams(location.search).has('token')) {
      // An email/text link still works without the sign-in, so reload it anonymously.
      location.reload();
    } else {
      location.assign(parentLoginPathWithNext(location.pathname, location.search));
    }
  };

  return onError(({ graphQLErrors, networkError, response, operation, forward }) => {
    if (!readSessionFailure({ graphQLErrors, networkError })) return undefined;

    if (tree !== SessionType.STAFF) {
      endSession();
      return undefined;
    }

    return new Observable((observer) => {
      let subscription: { unsubscribe: () => void } | undefined;

      if (!refreshing) {
        refreshing = deps.refreshStaffToken().finally(() => { refreshing = null; });
      }

      refreshing.then((token) => {
        // Only a dead credential ends the session. REFRESH_RETRY means the refresh never got an
        // answer — a 500 or a dropped connection — and destroying a live session over that turns a
        // recoverable blip into a sign-out on the rink.
        if (token === REFRESH_RETRY) {
          console.warn('Refresh did not answer; keeping the session and surfacing the original error');
          if (networkError) observer.error(networkError);
          else { observer.next(response || {}); observer.complete(); }
          return;
        }
        if (!token) {
          endSession();
          if (networkError) {
            observer.error(networkError);
          } else {
            observer.next(response || {});
            observer.complete();
          }
          return;
        }

        // onError never sees a retry's outcome, so a retry refused again ends the session here.
        subscription = forward(operation).subscribe({
          next: (result) => {
            if (readSessionFailure({ graphQLErrors: result.errors })) endSession();
            observer.next(result);
          },
          error: (error) => {
            if (readSessionFailure({ networkError: error })) endSession();
            observer.error(error);
          },
          complete: () => observer.complete(),
        });
      });

      return () => subscription && subscription.unsubscribe();
    });
  });
};
